/**
 * CapacitorBLEAdapter
 * Drop-in replacement for WebBluetoothAdapter from mxw01-thermal-printer.
 * Uses @capacitor-community/bluetooth-le for native Android BLE access.
 *
 * The mxw01-thermal-printer library expects:
 *   adapter.isAvailable()       → bool
 *   adapter.requestDevice()     → { id, name }
 *   adapter.connect(device)     → { controlCharacteristic, dataCharacteristic }
 *                                  each with writeValueWithoutResponse(buffer)
 */

import { BleClient } from '@capacitor-community/bluetooth-le'

const SERVICE_UUID    = '0000ae30-0000-1000-8000-00805f9b34fb'
const SERVICE_ALT     = '0000af30-0000-1000-8000-00805f9b34fb'
const CONTROL_UUID    = '0000ae01-0000-1000-8000-00805f9b34fb'
const NOTIFY_UUID     = '0000ae02-0000-1000-8000-00805f9b34fb'
const DATA_UUID       = '0000ae03-0000-1000-8000-00805f9b34fb'

export class CapacitorBLEAdapter {
  constructor() {
    this._deviceId = null
    this._initialized = false
  }

  async _ensureInit() {
    if (!this._initialized) {
      await BleClient.initialize()
      this._initialized = true
    }
  }

  isAvailable() {
    return true
  }

  async requestDevice() {
    await this._ensureInit()

    const savedId = localStorage.getItem('mxw01_device_id')
    if (savedId) {
      try {
        await BleClient.connect(savedId, () => this._onDisconnect())
        this._deviceId = savedId
        console.log('[BLE] Auto-connected to saved device:', savedId)
        return { id: savedId, name: localStorage.getItem('mxw01_device_name') || 'MXW01' }
      } catch (e) {
        console.warn('[BLE] Auto-connect failed, falling back to scan:', e)
        localStorage.removeItem('mxw01_device_id')
      }
    }

    const device = await BleClient.requestDevice({
      
      services: [SERVICE_UUID, SERVICE_ALT],
      optionalServices: [SERVICE_UUID, SERVICE_ALT],
    })

    this._deviceId = device.deviceId
    localStorage.setItem('mxw01_device_id', device.deviceId)
    localStorage.setItem('mxw01_device_name', device.name || 'MXW01')
    console.log('[BLE] Selected device:', device.name, device.deviceId)
    return { id: device.deviceId, name: device.name || 'MXW01' }
  }

  async connect(device) {
    const deviceId = device?.id || this._deviceId

    if (deviceId !== this._deviceId) {
      await BleClient.connect(deviceId, () => this._onDisconnect())
      this._deviceId = deviceId
    }

    console.log('[BLE] Building characteristic wrappers for', deviceId)

    const makeCharacteristic = (serviceUuid, charUuid) => ({
      writeValueWithoutResponse: async (buffer) => {
        const data = buffer instanceof DataView
          ? new Uint8Array(buffer.buffer)
          : new Uint8Array(buffer)
        // Auto-reconnect if disconnected
        if (!this._deviceId) {
          await BleClient.connect(deviceId, () => this._onDisconnect())
          this._deviceId = deviceId
        }
        await BleClient.writeWithoutResponse(
          deviceId,
          serviceUuid,
          charUuid,
          new DataView(data.buffer)
        )
        await new Promise(r => setTimeout(r, 0))
      },
      startNotifications: async (callback) => {
        await BleClient.startNotifications(deviceId, serviceUuid, charUuid, (data) => {
          callback({ target: { value: data } })
        })
      },
      addEventListener: (event, callback) => {
        if (event === 'characteristicvaluechanged') {
          BleClient.startNotifications(deviceId, serviceUuid, charUuid, (data) => {
            callback({ target: { value: data } })
          })
        }
      }
    })

    const serviceUuid = SERVICE_UUID

    return {
      controlCharacteristic: makeCharacteristic(serviceUuid, CONTROL_UUID),
      dataCharacteristic: makeCharacteristic(serviceUuid, DATA_UUID),
      notifyCharacteristic: makeCharacteristic(serviceUuid, NOTIFY_UUID),
    }
  }

  async disconnect(device) {
    const deviceId = device?.id || this._deviceId
    if (deviceId) {
      try {
        await BleClient.disconnect(deviceId)
      } catch (e) {
        console.warn('[BLE] Disconnect error:', e)
      }
    }
    this._deviceId = null
  }

  _onDisconnect() {
    console.warn('[BLE] Printer disconnected')
    this._deviceId = null
  }

  get isConnected() {
    return !!this._deviceId
  }
}